import { Species, SpeciesSurveyDataV1 } from '@omniafishing/core';
import _ from 'lodash';
import React, { useMemo } from 'react';
import { useReferenceData } from '../../../../../hooks/use_reference_data';
import { SpeciesCard } from './species_card';

export const DEFAULT_SPECIES_ORDER_BY_DISPLAY_NAME = [
  'Largemouth Bass',
  'Smallmouth Bass',
  'Walleye',
  'Crappie',
  'Spotted Bass',
  'Panfish / Bluegill',
  'Bluegill / Panfish',
  'Bluegill',
  'Panfish',
  'Northern Pike',
  'Trout',
  'Striped Bass',
  'Catfish',
  'Yellow Perch',
  'Perch',
  'White Bass',
  'Muskie',
  'Muskellunge',
  'Lake Trout',
  'Common Carp',
  'Drum',
  'Freshwater Drum',
  'Sturgeon',
  'Gar',
  'Salmon',
  'Sauger',
  'Burbot',
];

export const speciesSurveyDataKeysSort = (
  waterbodySpeciesSurveyData: SpeciesSurveyDataV1,
  topSpecies?: Species
) => {
  const speciesKeys = Object.keys(waterbodySpeciesSurveyData.species);

  return _.orderBy(
    speciesKeys,
    [
      // 1. Primary Sort: Put topSpecies first
      (speciesDisplayName) => (speciesDisplayName === topSpecies?.display_name ? -1 : 0),

      // 2. Secondary Sort: Sort by abundance_quartile descending (higher numbers first)
      //    Nulls/missing quartiles are treated as -1, effectively sorting them last in descending order.
      (speciesDisplayName) =>
        waterbodySpeciesSurveyData.species[speciesDisplayName]?.current_status
          ?.abundance_quartile ?? -1,

      // 3. Tertiary Sort: Use the DEFAULT_SPECIES_ORDER_BY_DISPLAY_NAME
      (speciesDisplayName) => {
        const index = DEFAULT_SPECIES_ORDER_BY_DISPLAY_NAME.indexOf(speciesDisplayName);
        // If found (index >= 0), return the index.
        // If not found (index === -1), return Infinity so it sorts after all found items.
        return index === -1 ? Infinity : index;
      },
      // 4. Quaternary Sort (Fallback): Alphabetical by name for stability
      //    This breaks ties if quartiles are equal AND either:
      //    a) Both species are in the default list at the same effective position (unlikely with unique names)
      //    b) Both species are NOT in the default list
      (speciesDisplayName) => speciesDisplayName,
    ],
    [
      'asc', // Sort topSpecies check ascending (-1 comes before 0)
      'desc', // Sort quartile descending
      'asc', // Sort default order index ascending (0, 1, 2... Infinity)
      'asc', // Sort fallback alphabetical ascending
    ]
  );
};

interface SpeciesListProps {
  waterbodySpeciesSurveyData: SpeciesSurveyDataV1;
  topSpecies: Species;
  onSpeciesSelect: (species: Species) => void;
}

export const SpeciesList = (props: SpeciesListProps) => {
  const { waterbodySpeciesSurveyData, topSpecies, onSpeciesSelect } = props;
  const { allSpecies } = useReferenceData();

  const speciesKeysSorted = useMemo(
    () => speciesSurveyDataKeysSort(waterbodySpeciesSurveyData, topSpecies),
    [waterbodySpeciesSurveyData, topSpecies]
  );

  return (
    <div className="flex flex-col gap-3 p-3">
      {speciesKeysSorted.map((key) => {
        const speciesData = waterbodySpeciesSurveyData.species[key];
        const species = allSpecies.find((s) => s.display_name === key);
        if (!species || !speciesData) {
          return null;
        }

        return (
          <div key={key}>
            <SpeciesCard species={species} speciesData={speciesData} onClick={onSpeciesSelect} />
          </div>
        );
      })}
    </div>
  );
};
